// The typed client. Every call parses its answer with the contract's schema; every failure becomes an ApiError with a code.
import {
  CarouselRequest,
  DeckDraftDto,
  DeckDraftRequest,
  VideoRequest,
  AccountAccepted,
  CompetitorAdDto,
  CompetitorDto,
  CompetitorSyncResult,
  CustomerPersonCreate,
  CustomerPersonPatch,
  CustomerSaveResult,
  ManualKnowledgeResult,
  NewsToTopicResponse,
  ProductNewsDto,
  AccountTenantList,
  AdAccountAssetsDto,
  AdAccountList,
  AdActionDto,
  AdDailyMetrics,
  AdProductAdDto,
  AdProductAdPage,
  AdProductOptionsDto,
  AdProductSetDto,
  AdRsaCheckDto,
  AdRsaDto,
  AdActionPage,
  AdAlertDto,
  AdAlertPage,
  AdAudienceDto,
  AdAudiencePage,
  AdAudienceRefreshDto,
  AdCampaignPage,
  AdCatalogDto,
  AdCatalogPage,
  AdCatalogSyncDto,
  AdCellDecisionDto,
  AdCellDecisionPage,
  AdCellDto,
  AdCellPage,
  AdConnectionDto,
  AdConnectionLimitsDto,
  AdConnectionPage,
  AdConnectionTestDto,
  AdEvaluationDto,
  AdFormDto,
  AdFormPage,
  AdFormTemplateList,
  AdGuardRunDto,
  AdGuardSettingsDto,
  AdGuardStatusDto,
  AdHoldDto,
  AdHoldPage,
  AdKeywordPage,
  AdMetricTotals,
  AdminHostDto,
  AdminHostList,
  type AdminHostPatch,
  type AdminLiveRequest,
  AdminLiveResponse,
  AdminTenantDto,
  AdminTenantPage,
  AdminAgencyPage,
  AgencyDto,
  AgencyMemberDto,
  AgencyMemberPage,
  AgencyTenantPage,
  AiUsageCallPage,
  AiUsageDaysResponse,
  AgencyBrandResponse,
  AdminUsageDto,
  AdOAuthStartDto,
  AdPage,
  AdPageList,
  AdPromotionDto,
  AdPromotionPage,
  AdScheduleSuggestionDto,
  AdSetPage,
  AdStartResultDto,
  AdSyncReportDto,
  AdWizardDto,
  AdWizardPage,
  AdWizardReviewDto,
  type AgencyTenantRequest,
  BillingDto,
  type BillingMode,
  BillingStatusDto,
  type CheckoutRequest,
  // s10-plans: the companies that pay and the workspace's plan.
  CancelResult,
  CheckoutResult,
  CompanyDto,
  CompanyList,
  CompanyLookupDto,
  type CompanyLookupQuery,
  type CompanyPatch,
  type MoveWorkspaceRequest,
  type NewCompanyRequest,
  type PlanChangeRequest,
  PlanChangeResult,
  CampaignCreateInput,
  CampaignDto,
  CampaignList,
  CampaignReportDto,
  CampaignScheduleInput,
  CampaignStatsDto,
  CampaignUpdateInput,
  ChannelSettingsInputDto,
  ConsentSummary,
  ConversionDto,
  ConversionPage,
  ConversionSummary,
  DirectSession,
  EmergencyStopResultDto,
  type LoginLinkRequest,
  PlatformAuditPage,
  PlatformEnrolled,
  PlatformLinkResult,
  PlatformOperatorDto,
  PlatformSession,
  PublicBrandDto,
  ReconciliationDto,
  RedirectDto,
  RestartAdsResult,
  type SecondFactorRequest,
  type LiftEmailStopRequest,
  type SetBillingModeRequest,
  type SetPlanRequest,
  type SetTenantStatusRequest,
  AccountDeviceList,
  AccountSignedIn,
  AccountSignedOut,
  SignInCodeAccepted,
  type AccountRefreshRequest,
  type SignInCodeRequest,
  type SignInCodeVerify,
  type WorkspaceRequest,
  CountrySettingsDto,
  CustomerCardDto,
  CustomerPage,
  DataSourcesDto,
  DryRunMessageList,
  ImportDto,
  ImportMapping,
  ImportPage,
  ImportPreviewDto,
  JourneyCreateInput,
  JourneyDefaultsResult,
  JourneyDto,
  JourneyEnrollInput,
  JourneyEnrollmentList,
  JourneyEnrollResult,
  JourneyManualCheckDto,
  JourneyManualEnrollDto,
  JourneyList,
  JourneyStatsDto,
  JourneyStepRunList,
  JourneyUpdateInput,
  MessageTemplateDto,
  AngleDto,
  AngleInput,
  AnglePage,
  AnglePatch,
  ProfileDto,
  ProfileInput,
  ProfilePage,
  ProfilePatch,
  TemplateCheckDto,
  TemplateDto,
  TemplatePage,
  TemplatePreviewDto,
  TemplateShelfDto,
  type TemplateCheckInput,
  type TemplateInput,
  type TemplatePatch,
  MessageTemplateList,
  MessageTemplatePreviewDto,
  EmailStandingDto,
  MessagingOverviewDto,
  ProposalAccept,
  ProposalDto,
  ProposalPage,
  ReferralCodeDto,
  ReferralProgramAnswer,
  ReferralProgramDto,
  ReferralProgramInput,
  ReferralStatsDto,
  SegmentCountDto,
  SegmentCreate,
  SegmentDto,
  SegmentOptionsDto,
  SegmentPage,
  SegmentPatch,
  SegmentPresetList,
  SegmentRules,
  SendingDomainDto,
  SendingDomainList,
  TemplateCreateInput,
  TemplateGenerateInput,
  TemplatePreviewInput,
  TemplateTestSendInput,
  TemplateTestSendResult,
  TemplateUpdateInput,
  TestEventResult,
  TrackingConfigDto,
  TrackingFunnel,
  ApproversDto,
  InvitationAcceptRequest,
  InvitationDto,
  InvitationPage,
  InvitationPreview,
  InvitationRequest,
  NotifyCellPatch,
  NotifyPhoneRequest,
  NotifyPreferencesDto,
  PushHandoffDto,
  PushSubscribedDto,
  PushSubscribeRequest,
  QuietHoursRequest,
  SetApproversRequest,
  WithdrawCreativeRequest,
  WithdrawCreativeResponse,
  TeamMemberDto,
  TeamMemberPage,
  TeamMemberPatch,
  TelegramLinkDto,
} from "@imagora/contracts";
import type { AdCampaignTargetsInput, AdHoldStatus, AdPlatform, BookingLinkInput, CustomArchetypeInput, CustomArchetypePatch, LeadFieldDefinitionInput, LeadFieldDefinitionPatch, LeadFunnelQuery } from "@imagora/contracts";
import {
  AdCampaignDto,
  LeadFieldDefinitionDto,
  ArchetypeDto,
  ArchetypeUsageDto,
  ConsentPeoplePage,
  DsarExportDto,
  DsarRequestDto,
  DsarRequestList,
  LandingPageDto,
  LandingPageList,
  BookingLinkDto,
  CalendarConnectUrl,
  LeadBookingList,
  LeadCalendarDto,
  LeadFunnelDto,
  LeadSequenceDto,
  LeadSequenceInput,
  LeadSequenceList,
  LeadSequencePatch,
  LeadSequencesView,
  LeadCard,
  LeadDto,
  LeadFormDto,
  LeadFormList,
  LeadIntakeResult,
  LeadNoteDto,
  LeadOwnerDto,
  LeadOwnerList,
  LeadPage,
  LeadScoringDto,
  LeadSendResult,
  LeadSettingsDto,
  LeadTaskDto,
  LeadTemplateDto,
  LeadTemplateList,
  PipelineDto,
  SuppressionAdded,
  SuppressionList,
  type LandingPageInput,
  type LandingPagePatch,
  type LeadContactInput,
  type LeadFormInput,
  type LeadFormPatch,
  type LeadInput,
  type LeadOwnerInput,
  type LeadPatch,
  type LeadScoringPatch,
  type LeadSendInput,
  type LeadSettingsPatch,
  type LeadTaskInput,
  type LeadTaskPatch,
  type LeadTemplateInput,
  type LeadTemplatePatch,
  type PipelineInput,
  type StageChangeInput,
  type SuppressionAddInput,
  ApiKeyCreated,
  ApiKeyDto,
  ApiKeyList,
  ApprovalLinkDto,
  AssetDto,
  AssetUsageDto,
  AgencyReportDto,
  AuditList,
  BrandPreviewResponse,
  BrandResponse,
  BulkApproveResponse,
  CalendarResponse,
  CaptureDomainDto,
  CaptureDto,
  CardCheckRequest,
  CardTemplateList,
  ChannelDto,
  ChannelHealthDto,
  ChannelNoticeDto,
  ChooseThemeResponse,
  CONTRACT_HEADER,
  CONTRACT_VERSION,
  CreativeDto,
  ErrorEnvelope,
  FactDto,
  FactPage,
  FunnelResponse,
  FrozenFactsDto,
  FillScheduleResponse,
  GenerateThemesResponse,
  GenerateTopicsResponse,
  GenerationRunDto,
  GoodHoursReport,
  GoodHoursResponse,
  HomeSummary,
  IllustrationOptions,
  ImageCheckDto,
  ImportSeedResponse,
  KnowledgeChangePage,
  KnowledgeItemPage,
  KnowledgeSummary,
  LiveSwitchesResponse,
  ManualCardDto,
  MeResponse,
  MondayReportDto,
  MondayReportPage,
  OAuthStartDto,
  OAuthTargetList,
  MarkManualPublishedInput,
  TelegramTokenInput,
  PauseImpactDto,
  PlanSettingsDto,
  PostMetricsHistory,
  PostMetricsPage,
  PromoteDraftFactResponse,
  PublicApprovalDto,
  PublishReceiptDto,
  QueueCardDto,
  ReadinessReportDto,
  ReconcileResponse,
  RenderedReport,
  ScheduleDto,
  ScoresResponse,
  ShortLinkDto,
  ShortLinkPage,
  ShortLinkQrDto,
  ShortLinkStats,
  SimilarTopicsResponse,
  SsoExchangeResponse,
  TenantDto,
  TenantSettingsDto,
  TenantSettingsPatchResponse,
  ThemeDto,
  TopicBulkResponse,
  OutgoingPage,
  ThemeOutputs,
  TopicCategoryDto,
  WeekPlanOutputs,
  TopicDto,
  TopicExclusionDto,
  TopicHookDto,
  TopicHookInput,
  TopicHookPatch,
  TopicHistoryResponse,
  VerticalDto,
  VisualRenderDto,
  WebhookDeliveryDetail,
  WebhookDeliveryDto,
  WebhookDeliveryPage,
  WebhookEndpointCreated,
  WebhookEndpointDto,
  WebhookEndpointList,
  WebhookSecretRotated,
  WeekPlanDto,
  pageOf,
  type CompetitorAdsQuery,
  type CompetitorInput,
  type CompetitorPatch,
  type ProductNewsInput,
  type ProductNewsListQuery,
  type ProductNewsPatch,
  type AnalyticsRangeQuery,
  type ApiKeyCreateRequest,
  type AssetListQuery,
  type AssetPatch,
  type AuditQuery,
  type BrandInput,
  type AdminTenantAgencyPut,
  type AgencyMemberPatch,
  type BrandPreviewRequest,
  type CalendarEventInput,
  type CalendarEventPatch,
  type CalendarOverrideInput,
  type CaptureCreate,
  type CaptureDomainCreate,
  type ChannelCreate,
  type ChannelUpdate,
  type CreateApprovalLinkRequest,
  type CreateShortLink,
  type EditCreativeRequest,
  type FactCreate,
  type FactListQuery,
  type FactPatch,
  type FunnelDimension,
  type GenerateRequest,
  type GenerateThemesRequest,
  type GenerateTopicsRequest,
  type IllustrationRequest,
  type KnowledgeKind,
  type LiveKind,
  type PageQuery,
  type PauseImpactRequest,
  type PlanSettingsPut,
  type ProductPhotoRequest,
  type ProposeWeekPlanRequest,
  type QueueListQuery,
  type ScheduleInput,
  type ScoreKind,
  type SchedulePatch,
  type TenantPatch,
  type TenantSettingsPatch,
  type ThemeListQuery,
  type ThemePatch,
  type TopicBulkRequest,
  type TopicCreate,
  type TopicListQuery,
  type TopicMergeRequest,
  type TopicPatch,
  type UpdateShortLink,
  type WebhookDeliveryListQuery,
  type WebhookEndpointCreate,
  type WebhookEndpointPatch,
  type ResolveReviewInput,
  type WeekPlanItemPatch,
  type WeekPlanListQuery,
} from "@imagora/contracts";
import { AdminAiGrantDto, AiKeyDto, AiKeysStatusDto, type AdminAiGrantPut, type AiKeyChoice, type AiKeyProvider, type AiKeySaveRequest } from "@imagora/contracts";
// s10-keys-b
import { AiKeyStepDto, SmsStatusDto, type SmsProviderSaveRequest } from "@imagora/contracts";
// s10-login-b
import {
  AccountDeletionStatus,
  AccountMethodList,
  HostLoginSettingsDto,
  OAuthCompleted,
  OAuthStarted,
  type AccountReauthRequest,
  type AccountReauthVerify,
  type HostLoginSettingsPut,
  type LoginProvider,
  type OAuthCompleteRequest,
  type OAuthEmailRequest,
  type OAuthSignInStartRequest,
  type OAuthStartRequest,
} from "@imagora/contracts";
// s10-mcp-a
import { ConnectDecisionDto, ConnectRequestDto, McpConnectionDto, McpConnectionList, McpConnectionPage, type ConnectDecisionRequest } from "@imagora/contracts";
import { z } from "zod";

const LeadFieldList = z.object({ items: z.array(LeadFieldDefinitionDto) });

/** What narrows a list of leads, and an export of it. */
export type LeadFilterQuery = {
  ownerId?: string;
  q?: string;
  fieldKey?: string;
  fieldValue?: string;
  fieldFrom?: string;
  fieldTo?: string;
};

const Anything = z.unknown();
const TopicPage = pageOf(TopicDto);
const CompetitorList = z.object({ items: z.array(CompetitorDto) });
const CompetitorAdPage = pageOf(CompetitorAdDto);
const CompetitorSyncAnswer = z.object({ results: z.array(CompetitorSyncResult), skipped: z.string().nullable() });
const ProductNewsPage = pageOf(ProductNewsDto);
const ThemePage = pageOf(ThemeDto);
const WeekPlanPage = pageOf(WeekPlanDto);
const TopicList = z.object({ items: z.array(TopicDto) });
const TopicCategoryList = z.object({ items: z.array(TopicCategoryDto) });
const RetryPlanItemResponse = z.object({ runId: z.string() });
const ExclusionList = z.object({ items: z.array(TopicExclusionDto) });
const HookList = z.object({ items: z.array(TopicHookDto) });
const CreativePage = pageOf(CreativeDto);
const RunList = z.object({ items: z.array(GenerationRunDto) });
const SchedulePage = z.object({ items: z.array(ScheduleDto) });
const AssetPage = pageOf(AssetDto);
const AssetUsagePage = pageOf(AssetUsageDto);
const CapturePage = pageOf(CaptureDto);
const CaptureDomainList = z.object({ items: z.array(CaptureDomainDto) });
const ChannelPage = pageOf(ChannelDto);
const ChannelNoticePage = pageOf(ChannelNoticeDto);
const ReceiptPage = pageOf(PublishReceiptDto);
const ManualCardPage = pageOf(ManualCardDto);
const Decided = z.object({ blocksApproval: z.boolean().optional() }).passthrough();
const seg = encodeURIComponent;

export class ApiError extends Error {
  constructor(
    readonly code: string,
    readonly status: number,
    readonly details?: unknown,
    readonly requestId?: string,
  ) {
    super(code);
    this.name = "ApiError";
  }
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  query?: Record<string, string | number | undefined>;
  /** Exchange calls carry no session and are never retried through a refresh. */
  anonymous?: boolean;
  /** A token saved from an earlier page load, checked before the store holds it. */
  bearer?: string;
  /** Sends the cookie the API set, from the app's own origin only (see `carriesCookie`): the calls of a device that stays signed in. */
  credentials?: boolean;
}

export interface ApiClientOptions {
  /** Empty means same origin. */
  baseUrl: string;
  getToken: () => string | null;
  /** Gets a new session after a 401. Resolves false when none could be had. */
  refreshSession: () => Promise<boolean>;
  fetch?: typeof fetch;
  /** Where the page runs, and the app's own origin. Only the second may send the cookie; elsewhere the session is the tab's. */
  pageOrigin?: string;
  appOrigin?: string;
}

/**
 * The API answers a host's domain without `Access-Control-Allow-Credentials`, as it should, so a credentialed call from there is dropped
 * by the browser: the letter arrives and nothing signs in. Credentials go only from the app's own origin.
 */
export function carriesCookie(pageOrigin: string | undefined, appOrigin: string | undefined): boolean {
  return Boolean(pageOrigin && appOrigin && pageOrigin === appOrigin);
}

const REFRESHABLE_CODES = new Set(["session_expired", "session_invalid", "unauthorized"]);

async function readError(res: Response): Promise<ApiError> {
  const requestId = res.headers.get("x-request-id") ?? undefined;
  try {
    const parsed = ErrorEnvelope.safeParse(await res.json());
    if (parsed.success) return new ApiError(parsed.data.error.code, res.status, parsed.data.error.details, requestId);
  } catch {
    // Not JSON: a proxy or a crash answered. The status is all we have.
  }
  return new ApiError(res.status >= 500 ? "internal_error" : "http_error", res.status, undefined, requestId);
}

export function createApiClient(options: ApiClientOptions) {
  const doFetch = options.fetch ?? ((input, init) => globalThis.fetch(input, init));
  const ownOrigin = carriesCookie(options.pageOrigin, options.appOrigin);

  async function send(path: string, init: RequestOptions): Promise<Response> {
    const url = new URL(`${options.baseUrl}${path}`, options.baseUrl ? undefined : "http://same-origin");
    for (const [key, value] of Object.entries(init.query ?? {})) {
      if (value !== undefined && value !== "") url.searchParams.set(key, String(value));
    }
    const headers: Record<string, string> = { [CONTRACT_HEADER]: CONTRACT_VERSION };
    if (init.body !== undefined) headers["Content-Type"] = "application/json";
    const token = init.bearer ?? (init.anonymous ? null : options.getToken());
    if (token) headers.Authorization = `Bearer ${token}`;
    try {
      return await doFetch(options.baseUrl ? url.href : `${url.pathname}${url.search}`, {
        method: init.method ?? "GET",
        headers,
        ...(init.credentials && ownOrigin ? { credentials: "include" as const } : {}),
        ...(init.body === undefined ? {} : { body: JSON.stringify(init.body) }),
      });
    } catch {
      throw new ApiError("network_error", 0);
    }
  }

  async function request<T extends z.ZodType>(schema: T, path: string, init: RequestOptions = {}): Promise<z.output<T>> {
    let res = await send(path, init);
    // A call with its own token is part of a renewal: asking for a renewal here would wait on itself.
    if (res.status === 401 && !init.anonymous && !init.bearer) {
      const error = await readError(res);
      if (!REFRESHABLE_CODES.has(error.code) || !(await options.refreshSession())) throw error;
      res = await send(path, init);
    }
    if (!res.ok) throw await readError(res);
    const parsed = schema.safeParse(await res.json().catch(() => undefined));
    if (!parsed.success) throw new ApiError("contract_mismatch", res.status);
    return parsed.data;
  }

  /** What is not JSON going in or coming out: a file upload, a rendered picture. */
  async function sendRaw(path: string, init: { method: "POST"; form?: FormData; json?: unknown; file?: Blob }): Promise<Response> {
    const attempt = async () => {
      const headers: Record<string, string> = { [CONTRACT_HEADER]: CONTRACT_VERSION };
      if (init.json !== undefined) headers["Content-Type"] = "application/json";
      const token = options.getToken();
      if (token) headers.Authorization = `Bearer ${token}`;
      try {
        const body = init.form ?? init.file ?? (init.json === undefined ? null : JSON.stringify(init.json));
        return await doFetch(`${options.baseUrl}${path}`, { method: init.method, headers, ...(body === null ? {} : { body }) });
      } catch {
        throw new ApiError("network_error", 0);
      }
    };
    let res = await attempt();
    if (res.status === 401) {
      const error = await readError(res);
      if (!REFRESHABLE_CODES.has(error.code) || !(await options.refreshSession())) throw error;
      res = await attempt();
    }
    if (!res.ok) throw await readError(res);
    return res;
  }

  /** For an answer with no body, such as a 204. */
  async function sendEmpty(path: string, method: "DELETE"): Promise<void> {
    await request(Anything, path, { method });
  }

  return {
    request,
    exchange: (token: string) => request(SsoExchangeResponse, "/v1/sso/exchange", { method: "POST", body: { token }, anonymous: true }),
    me: () => request(MeResponse, "/v1/me"),
    tenant: {
      get: () => request(TenantDto, "/v1/tenant"),
      update: (patch: TenantPatch) => request(TenantDto, "/v1/tenant", { method: "PATCH", body: patch }),
      readiness: () => request(ReadinessReportDto, "/v1/tenant/readiness"),
    },
    liveSwitches: {
      list: () => request(LiveSwitchesResponse, "/v1/live-switches"),
      set: (kind: LiveKind, enabled: boolean) => request(LiveSwitchesResponse, "/v1/live-switches", { method: "PUT", body: { kind, enabled, level: "tenant" } }),
    },
    apiKeys: {
      list: (page: Partial<PageQuery>) => request(ApiKeyList, "/v1/api-keys", { query: page }),
      create: (input: Pick<ApiKeyCreateRequest, "name" | "scopes">) => request(ApiKeyCreated, "/v1/api-keys", { method: "POST", body: input }),
      revoke: (id: string) => request(ApiKeyDto, `/v1/api-keys/${encodeURIComponent(id)}`, { method: "DELETE" }),
    },
    // s10-mcp-b: the workspace's assistant connections, everyone's, for its owner on „Chei API”.
    oauthConnections: {
      list: (page: Partial<PageQuery>) => request(McpConnectionPage, "/v1/oauth-connections", { query: page }),
      revoke: (id: string) => request(McpConnectionDto, `/v1/oauth-connections/${seg(id)}`, { method: "DELETE" }),
    },
    audit: {
      list: (query: Partial<AuditQuery>) => request(AuditList, "/v1/audit", { query }),
    },
    analytics: {
      home: () => request(HomeSummary, "/v1/analytics/home"),
      funnel: (query: AnalyticsRangeQuery & { dimension: FunnelDimension; limit?: number }) => request(FunnelResponse, "/v1/analytics/funnel", { query }),
      scores: (kind: ScoreKind) => request(ScoresResponse, "/v1/analytics/scores", { query: { kind, limit: 50 } }),
      goodHours: () => request(GoodHoursReport, "/v1/analytics/good-hours"),
      socialMetrics: (query: AnalyticsRangeQuery & { cursor?: string; limit?: number }) => request(PostMetricsPage, "/v1/analytics/social-metrics", { query }),
      postHistory: (postId: string) => request(PostMetricsHistory, `/v1/analytics/social-metrics/${seg(postId)}`),
      reconcile: () => request(ReconcileResponse, "/v1/analytics/reconcile"),
    },
    reports: {
      list: (query: { cursor?: string; limit?: number }) => request(MondayReportPage, "/v1/reports/monday", { query }),
      week: (weekStart: string) => request(MondayReportDto, `/v1/reports/monday/${seg(weekStart)}`),
      latest: () => request(MondayReportDto, "/v1/reports/monday/latest"),
      rendered: (weekStart: string) => request(RenderedReport, `/v1/reports/monday/${seg(weekStart)}/rendered`),
      generate: () => request(MondayReportDto, "/v1/reports/monday/generate", { method: "POST" }),
      agency: (agencyId: string, week?: string) => request(AgencyReportDto, `/v1/reports/agency/${seg(agencyId)}`, { query: { week } }),
    },
    links: {
      list: (query: { cursor?: string; limit?: number }) => request(ShortLinkPage, "/v1/links", { query }),
      create: (input: CreateShortLink) => request(ShortLinkDto, "/v1/links", { method: "POST", body: input }),
      update: (id: string, patch: UpdateShortLink) => request(ShortLinkDto, `/v1/links/${seg(id)}`, { method: "PATCH", body: patch }),
      qr: (id: string, size = 512) => request(ShortLinkQrDto, `/v1/links/${seg(id)}/qr`, { query: { size } }),
      stats: (id: string, query: AnalyticsRangeQuery) => request(ShortLinkStats, `/v1/links/${seg(id)}/stats`, { query }),
    },
    settings: {
      get: () => request(TenantSettingsDto, "/v1/tenant/settings"),
      /** Answers 200, or 207 when some sections changed and others did not: `failed` says which. */
      patch: (patch: TenantSettingsPatch) => request(TenantSettingsPatchResponse, "/v1/tenant/settings", { method: "PATCH", body: patch }),
    },
    webhooks: {
      list: () => request(WebhookEndpointList, "/v1/webhook-endpoints"),
      create: (input: WebhookEndpointCreate) => request(WebhookEndpointCreated, "/v1/webhook-endpoints", { method: "POST", body: input }),
      update: (id: string, patch: WebhookEndpointPatch) => request(WebhookEndpointDto, `/v1/webhook-endpoints/${seg(id)}`, { method: "PATCH", body: patch }),
      rotate: (id: string, graceHours: number) => request(WebhookSecretRotated, `/v1/webhook-endpoints/${seg(id)}/rotate-secret`, { method: "POST", body: { graceHours } }),
      deliveries: (query: Partial<WebhookDeliveryListQuery>) => request(WebhookDeliveryPage, "/v1/webhook-deliveries", { query }),
      delivery: (id: string) => request(WebhookDeliveryDetail, `/v1/webhook-deliveries/${seg(id)}`),
      replay: (id: string) => request(WebhookDeliveryDto, `/v1/webhook-deliveries/${seg(id)}/replay`, { method: "POST" }),
    },
    knowledge: {
      summary: (tenantId: string) => request(KnowledgeSummary, `/v1/tenants/${encodeURIComponent(tenantId)}/knowledge`),
      items: (tenantId: string, kind: KnowledgeKind, page: Partial<PageQuery>) => request(KnowledgeItemPage, `/v1/tenants/${encodeURIComponent(tenantId)}/knowledge/${kind}`, { query: page }),
      addItem: (kind: KnowledgeKind, item: Record<string, unknown>) => request(ManualKnowledgeResult, `/v1/knowledge/${seg(kind)}/items`, { method: "POST", body: { item } }),
      saveItem: (kind: KnowledgeKind, key: string, item: Record<string, unknown>) => request(ManualKnowledgeResult, `/v1/knowledge/${seg(kind)}/items/${seg(key)}`, { method: "PUT", body: { item } }),
      removeItem: (kind: KnowledgeKind, key: string) => sendEmpty(`/v1/knowledge/${seg(kind)}/items/${seg(key)}`, "DELETE"),
      changes: (tenantId: string, kind: KnowledgeKind, page: Partial<PageQuery>) => request(KnowledgeChangePage, `/v1/tenants/${encodeURIComponent(tenantId)}/knowledge/${kind}/changes`, { query: page }),
    },
    vertical: () => request(VerticalDto, "/v1/vertical"),
    archetypes: {
      create: (input: CustomArchetypeInput) => request(ArchetypeDto, "/v1/archetypes", { method: "POST", body: input }),
      update: (id: string, patch: CustomArchetypePatch) => request(ArchetypeDto, `/v1/archetypes/${seg(id)}`, { method: "PATCH", body: patch }),
      usage: (id: string) => request(ArchetypeUsageDto, `/v1/archetypes/${seg(id)}/usage`),
      remove: (id: string) => sendEmpty(`/v1/archetypes/${seg(id)}`, "DELETE"),
    },
    facts: {
      list: (query: Partial<FactListQuery>) => request(FactPage, "/v1/facts", { query }),
      usable: () => request(FrozenFactsDto, "/v1/facts/generation", { query: { limit: 1000 } }),
      create: (input: FactCreate) => request(FactDto, "/v1/facts", { method: "POST", body: input }),
      update: (id: string, patch: FactPatch) => request(FactDto, `/v1/facts/${encodeURIComponent(id)}`, { method: "PATCH", body: patch }),
      approve: (id: string) => request(FactDto, `/v1/facts/${encodeURIComponent(id)}/approve`, { method: "POST" }),
      retire: (id: string) => request(FactDto, `/v1/facts/${encodeURIComponent(id)}/retire`, { method: "POST" }),
      remove: (id: string) => sendEmpty(`/v1/facts/${encodeURIComponent(id)}`, "DELETE"),
    },
    brand: {
      get: () => request(BrandResponse, "/v1/brand"),
      put: (input: BrandInput) => request(BrandResponse, "/v1/brand", { method: "PUT", body: input }),
      preview: (input: BrandPreviewRequest) => request(BrandPreviewResponse, "/v1/brand/preview", { method: "POST", body: input }),
    },
    topics: {
      list: (query: Partial<TopicListQuery>) => request(TopicPage, "/v1/topics", { query }),
      get: (id: string) => request(TopicDto, `/v1/topics/${encodeURIComponent(id)}`),
      create: (input: TopicCreate) => request(TopicDto, "/v1/topics", { method: "POST", body: input }),
      update: (id: string, patch: TopicPatch) => request(TopicDto, `/v1/topics/${encodeURIComponent(id)}`, { method: "PATCH", body: patch }),
      bulk: (input: TopicBulkRequest) => request(TopicBulkResponse, "/v1/topics/bulk", { method: "POST", body: input }),
      merge: (input: TopicMergeRequest) => request(TopicDto, "/v1/topics/merge", { method: "POST", body: input }),
      similar: (id: string) => request(SimilarTopicsResponse, `/v1/topics/${encodeURIComponent(id)}/similar`),
      similarToTitle: (title: string) => request(SimilarTopicsResponse, "/v1/topics/similar", { query: { title } }),
      history: (id: string) => request(TopicHistoryResponse, `/v1/topics/${encodeURIComponent(id)}/history`),
      generate: (input: GenerateTopicsRequest) => request(GenerateTopicsResponse, "/v1/topics/generate", { method: "POST", body: input }),
      keep: (keep: string[], discard: string[]) => request(TopicList, "/v1/topics/keep", { method: "POST", body: { keep, discard } }),
      promoteDraft: (id: string, index: number) => request(PromoteDraftFactResponse, `/v1/topics/${encodeURIComponent(id)}/promote-draft-fact`, { method: "POST", body: { index } }),
      importSeed: () => request(ImportSeedResponse, "/v1/topics/import-seed", { method: "POST" }),
      categories: () => request(TopicCategoryList, "/v1/topic-categories"),
      createCategory: (name: string) => request(TopicCategoryDto, "/v1/topic-categories", { method: "POST", body: { name } }),
      updateCategory: (id: string, patch: { name: string }) => request(TopicCategoryDto, `/v1/topic-categories/${encodeURIComponent(id)}`, { method: "PATCH", body: patch }),
      hooks: () => request(HookList, "/v1/topic-hooks"),
      createHook: (input: TopicHookInput) => request(TopicHookDto, "/v1/topic-hooks", { method: "POST", body: input }),
      updateHook: (id: string, patch: TopicHookPatch) => request(TopicHookDto, `/v1/topic-hooks/${encodeURIComponent(id)}`, { method: "PATCH", body: patch }),
      exclusions: () => request(ExclusionList, "/v1/topic-exclusions"),
      createExclusion: (text: string, note: string | null) => request(TopicExclusionDto, "/v1/topic-exclusions", { method: "POST", body: { text, note } }),
      setExclusionStatus: (id: string, status: "active" | "archived") => request(TopicExclusionDto, `/v1/topic-exclusions/${encodeURIComponent(id)}`, { method: "PATCH", body: { status } }),
    },
    themes: {
      list: (query: Partial<ThemeListQuery>) => request(ThemePage, "/v1/themes", { query }),
      generate: (input: GenerateThemesRequest) => request(GenerateThemesResponse, "/v1/themes/generate", { method: "POST", body: input }),
      update: (id: string, patch: ThemePatch) => request(ThemeDto, `/v1/themes/${encodeURIComponent(id)}`, { method: "PATCH", body: patch }),
      choose: (id: string) => request(ChooseThemeResponse, `/v1/themes/${encodeURIComponent(id)}/choose`, { method: "POST" }),
      reject: (id: string) => request(ThemeDto, `/v1/themes/${encodeURIComponent(id)}/reject`, { method: "POST" }),
      outputs: (id: string) => request(ThemeOutputs, `/v1/themes/${encodeURIComponent(id)}/outputs`),
    },
    calendar: {
      list: (from: string, to: string, includeHidden: boolean) => request(CalendarResponse, "/v1/calendar", { query: { from, to, ...(includeHidden ? { includeHidden: "true" } : {}) } }),
      outgoing: (query: { from: string; to: string; limit?: number; cursor?: string }) => request(OutgoingPage, "/v1/calendar/outgoing", { query }),
      create: (input: CalendarEventInput) => request(Anything, "/v1/calendar/events", { method: "POST", body: input }),
      update: (id: string, patch: CalendarEventPatch) => request(Anything, `/v1/calendar/events/${encodeURIComponent(id)}`, { method: "PATCH", body: patch }),
      remove: (id: string) => sendEmpty(`/v1/calendar/events/${encodeURIComponent(id)}`, "DELETE"),
      override: (key: string, input: CalendarOverrideInput) => request(Anything, `/v1/calendar/overrides/${encodeURIComponent(key)}`, { method: "PUT", body: input }),
    },
    weekPlans: {
      list: (query: Partial<WeekPlanListQuery>) => request(WeekPlanPage, "/v1/week-plans", { query }),
      propose: (input: ProposeWeekPlanRequest) => request(WeekPlanDto, "/v1/week-plans/propose", { method: "POST", body: input }),
      updateItem: (itemId: string, patch: WeekPlanItemPatch) => request(WeekPlanDto, `/v1/week-plans/items/${encodeURIComponent(itemId)}`, { method: "PATCH", body: patch }),
      approve: (id: string) => request(WeekPlanDto, `/v1/week-plans/${encodeURIComponent(id)}/approve`, { method: "POST" }),
      reject: (id: string, reason?: string) => request(WeekPlanDto, `/v1/week-plans/${encodeURIComponent(id)}/reject`, { method: "POST", body: reason ? { reason } : {} }),
      settings: () => request(PlanSettingsDto, "/v1/week-plans/settings"),
      updateSettings: (input: PlanSettingsPut) => request(PlanSettingsDto, "/v1/week-plans/settings", { method: "PUT", body: input }),
      outputs: (id: string) => request(WeekPlanOutputs, `/v1/week-plans/${encodeURIComponent(id)}/outputs`),
      retryItem: (itemId: string) => request(RetryPlanItemResponse, `/v1/week-plans/items/${encodeURIComponent(itemId)}/retry`, { method: "POST" }),
    },
    studio: {
      generate: (input: GenerateRequest) => request(GenerationRunDto, "/v1/studio/generate", { method: "POST", body: input }),
      runs: () => request(RunList, "/v1/studio/runs"),
      run: (id: string) => request(GenerationRunDto, `/v1/studio/runs/${seg(id)}`),
      regenerate: (id: string, note?: string) => request(CreativeDto, `/v1/studio/creatives/${seg(id)}/regenerate`, { method: "POST", body: note ? { note } : {} }),
      goodHours: () => request(GoodHoursResponse, "/v1/studio/good-hours"),
    },
    queue: {
      list: (query: Partial<Omit<QueueListQuery, "status">> & { status?: string }) => request(CreativePage, "/v1/queue", { query: query as Record<string, string | number | undefined> }),
      card: (id: string) => request(QueueCardDto, `/v1/queue/${seg(id)}`),
      pauseImpact: (input: PauseImpactRequest) => request(PauseImpactDto, "/v1/queue/pause-impact", { method: "POST", body: input }),
      approve: (id: string, input: { revision?: number; scheduledFor?: string }) => request(CreativeDto, `/v1/queue/${seg(id)}/approve`, { method: "POST", body: input }),
      reject: (id: string, input: { reason: string; revision?: number }) => request(CreativeDto, `/v1/queue/${seg(id)}/reject`, { method: "POST", body: input }),
      edit: (id: string, input: EditCreativeRequest) => request(CreativeDto, `/v1/queue/${seg(id)}/edit`, { method: "POST", body: input }),
      schedule: (id: string, scheduledFor: string | null) => request(CreativeDto, `/v1/queue/${seg(id)}/schedule`, { method: "POST", body: { scheduledFor } }),
      retry: (id: string) => request(CreativeDto, `/v1/queue/${seg(id)}/retry`, { method: "POST" }),
      withdraw: (id: string, input: WithdrawCreativeRequest = {}) => request(WithdrawCreativeResponse, `/v1/queue/${seg(id)}/withdraw`, { method: "POST", body: input }),
      bulkApprove: (ids: string[]) => request(BulkApproveResponse, "/v1/queue/bulk-approve", { method: "POST", body: { ids } }),
      createLink: (id: string, input: CreateApprovalLinkRequest) => request(ApprovalLinkDto, `/v1/queue/${seg(id)}/links`, { method: "POST", body: input }),
    },
    publicApproval: {
      read: (token: string) => request(PublicApprovalDto, `/v1/public/approval/${seg(token)}`, { anonymous: true }),
      approve: (token: string, revision: number) => request(Decided, `/v1/public/approval/${seg(token)}/approve`, { method: "POST", body: { revision }, anonymous: true }),
      reject: (token: string, reason: string) => request(Decided, `/v1/public/approval/${seg(token)}/reject`, { method: "POST", body: { reason }, anonymous: true }),
      edit: (token: string, input: { body: string; hashtags?: string[]; firstComment?: string | null }) => request(Decided, `/v1/public/approval/${seg(token)}/edit`, { method: "POST", body: input, anonymous: true }),
    },
    schedules: {
      list: () => request(SchedulePage, "/v1/schedules"),
      create: (input: ScheduleInput) => request(ScheduleDto, "/v1/schedules", { method: "POST", body: input }),
      update: (id: string, patch: SchedulePatch) => request(ScheduleDto, `/v1/schedules/${seg(id)}`, { method: "PATCH", body: patch }),
      remove: (id: string) => sendEmpty(`/v1/schedules/${seg(id)}`, "DELETE"),
      fill: (id: string) => request(FillScheduleResponse, `/v1/schedules/${seg(id)}/fill`, { method: "POST", body: {} }),
    },
    assets: {
      list: (query: Partial<AssetListQuery>) => request(AssetPage, "/v1/assets", { query: query as Record<string, string | number | undefined> }),
      get: (id: string) => request(AssetDto, `/v1/assets/${seg(id)}`),
      upload: async (form: FormData) => {
        const parsed = AssetDto.safeParse(await (await sendRaw("/v1/assets", { method: "POST", form })).json().catch(() => undefined));
        if (!parsed.success) throw new ApiError("contract_mismatch", 200);
        return parsed.data;
      },
      uploadFile: async (form: FormData) => {
        const parsed = VisualRenderDto.safeParse(await (await sendRaw("/v1/assets/files", { method: "POST", form })).json().catch(() => undefined));
        if (!parsed.success) throw new ApiError("contract_mismatch", 200);
        return parsed.data;
      },
      update: (id: string, patch: AssetPatch) => request(AssetDto, `/v1/assets/${seg(id)}`, { method: "PATCH", body: patch }),
      retire: (id: string) => request(AssetDto, `/v1/assets/${seg(id)}/retire`, { method: "POST" }),
      restore: (id: string) => request(AssetDto, `/v1/assets/${seg(id)}/restore`, { method: "POST" }),
      usages: (id: string) => request(AssetUsagePage, `/v1/assets/${seg(id)}/usages`),
    },
    captures: {
      list: () => request(CapturePage, "/v1/captures"),
      create: (input: CaptureCreate) => request(CaptureDto, "/v1/captures", { method: "POST", body: input }),
      get: (id: string) => request(CaptureDto, `/v1/captures/${seg(id)}`),
      domains: () => request(CaptureDomainList, "/v1/captures/domains"),
      addDomain: (input: CaptureDomainCreate) => request(CaptureDomainDto, "/v1/captures/domains", { method: "POST", body: input }),
      removeDomain: (id: string) => sendEmpty(`/v1/captures/domains/${seg(id)}`, "DELETE"),
    },
    cards: {
      templates: () => request(CardTemplateList, "/v1/cards/templates"),
      preview: async (spec: z.input<typeof CardCheckRequest>): Promise<Blob> => (await sendRaw("/v1/cards/preview", { method: "POST", json: spec })).blob(),
      check: (spec: z.input<typeof CardCheckRequest>) => request(ImageCheckDto, "/v1/cards/check", { method: "POST", body: spec }),
      render: (spec: z.input<typeof CardCheckRequest>) => request(VisualRenderDto, "/v1/cards", { method: "POST", body: spec }),
      get: (id: string) => request(VisualRenderDto, `/v1/cards/${seg(id)}`),
      productPhoto: (input: ProductPhotoRequest) => request(VisualRenderDto, "/v1/cards/product-photo", { method: "POST", body: input }),
    },
    visuals: {
      illustrationOptions: () => request(IllustrationOptions, "/v1/visuals/illustrations/options"),
      illustration: (input: IllustrationRequest) => request(VisualRenderDto, "/v1/visuals/illustrations", { method: "POST", body: input }),
      job: (id: string) => request(VisualRenderDto, `/v1/visuals/jobs/${seg(id)}`),
      cancel: (id: string) => request(VisualRenderDto, `/v1/visuals/jobs/${seg(id)}/cancel`, { method: "POST" }),
      carousel: (input: z.input<typeof CarouselRequest>) => request(VisualRenderDto, "/v1/visuals/carousels", { method: "POST", body: input }),
      video: (input: z.input<typeof VideoRequest>) => request(VisualRenderDto, "/v1/visuals/videos", { method: "POST", body: input }),
      draft: (input: z.input<typeof DeckDraftRequest>) => request(DeckDraftDto, "/v1/visuals/slides/draft", { method: "POST", body: input }),
    },
    channels: {
      list: () => request(ChannelPage, "/v1/channels", { query: { limit: 100 } }),
      create: (input: ChannelCreate) => request(ChannelDto, "/v1/channels", { method: "POST", body: input }),
      update: (id: string, patch: ChannelUpdate) => request(ChannelDto, `/v1/channels/${seg(id)}`, { method: "PATCH", body: patch }),
      remove: (id: string) => sendEmpty(`/v1/channels/${seg(id)}`, "DELETE"),
      test: (id: string) => request(ChannelHealthDto, `/v1/channels/${seg(id)}/test`, { method: "POST" }),
      disconnect: (id: string) => request(ChannelDto, `/v1/channels/${seg(id)}/disconnect`, { method: "POST" }),
      telegramToken: (id: string, input: z.input<typeof TelegramTokenInput>) => request(ChannelDto, `/v1/channels/${seg(id)}/telegram-token`, { method: "POST", body: input }),
      oauthStart: (id: string) => request(OAuthStartDto, `/v1/channels/${seg(id)}/oauth/start`, { method: "POST" }),
      targets: (id: string) => request(OAuthTargetList, `/v1/channels/${seg(id)}/targets`),
      selectTarget: (id: string, externalId: string) => request(ChannelDto, `/v1/channels/${seg(id)}/select-target`, { method: "POST", body: { externalId } }),
      notices: (channelId?: string) => request(ChannelNoticePage, "/v1/channel-notices", { query: { open: "true", ...(channelId ? { channelId } : {}) } }),
      ackNotice: (id: string) => request(ChannelNoticeDto, `/v1/channel-notices/${seg(id)}/ack`, { method: "POST" }),
    },
    leadFields: {
      list: () => request(LeadFieldList, "/v1/lead-fields"),
      add: (input: LeadFieldDefinitionInput) => request(LeadFieldDefinitionDto, "/v1/lead-fields", { method: "POST", body: input }),
      patch: (id: string, patch: LeadFieldDefinitionPatch) => request(LeadFieldDefinitionDto, `/v1/lead-fields/${seg(id)}`, { method: "PATCH", body: patch }),
      order: (keys: string[]) => request(LeadFieldList, "/v1/lead-fields/order", { method: "PUT", body: { keys } }),
      remove: (id: string) => sendEmpty(`/v1/lead-fields/${seg(id)}`, "DELETE"),
    },
    leads: {
      exportCsv: async (query: LeadFilterQuery): Promise<{ csv: string; count: number; truncated: boolean }> => {
        const res = await send("/v1/leads/export.csv", { query });
        if (!res.ok) throw await readError(res);
        return { csv: await res.text(), count: Number(res.headers.get("x-imagora-export-count") ?? 0), truncated: res.headers.get("x-imagora-export-truncated") === "true" };
      },
      list: (query: LeadFilterQuery & { order?: "recent" | "urgency"; stage?: string; cursor?: string; limit?: number }) => request(LeadPage, "/v1/leads", { query }),
      add: (input: z.input<typeof LeadInput>) => request(LeadIntakeResult, "/v1/leads", { method: "POST", body: input }),
      card: (id: string) => request(LeadCard, `/v1/leads/${seg(id)}`),
      patch: (id: string, patch: LeadPatch) => request(LeadDto, `/v1/leads/${seg(id)}`, { method: "PATCH", body: patch }),
      stage: (id: string, input: StageChangeInput) => request(LeadDto, `/v1/leads/${seg(id)}/stage`, { method: "POST", body: input }),
      contact: (id: string, input: LeadContactInput) => request(LeadDto, `/v1/leads/${seg(id)}/contact`, { method: "POST", body: input }),
      note: (id: string, body: string) => request(LeadNoteDto, `/v1/leads/${seg(id)}/notes`, { method: "POST", body: { body } }),
      addTask: (id: string, input: LeadTaskInput) => request(LeadTaskDto, `/v1/leads/${seg(id)}/tasks`, { method: "POST", body: input }),
      patchTask: (id: string, taskId: string, patch: LeadTaskPatch) => request(LeadTaskDto, `/v1/leads/${seg(id)}/tasks/${seg(taskId)}`, { method: "PATCH", body: patch }),
      send: (id: string, input: LeadSendInput) => request(LeadSendResult, `/v1/leads/${seg(id)}/send`, { method: "POST", body: input }),
      merge: (id: string, absorbedId: string) => request(LeadDto, `/v1/leads/${seg(id)}/merge`, { method: "POST", body: { absorbedId } }),
      sequences: (id: string) => request(LeadSequencesView, `/v1/leads/${seg(id)}/sequences`),
      enroll: (id: string, sequenceId: string) => request(Anything, `/v1/leads/${seg(id)}/enroll`, { method: "POST", body: { sequenceId } }),
      stopSequence: (id: string, enrollmentId: string) => request(LeadSequencesView, `/v1/leads/${seg(id)}/sequences/${seg(enrollmentId)}/stop`, { method: "POST", body: {} }),
      bookingLink: (id: string, input: BookingLinkInput = {}) => request(BookingLinkDto, `/v1/leads/${seg(id)}/booking-link`, { method: "POST", body: input }),
      bookings: (id: string) => request(LeadBookingList, `/v1/leads/${seg(id)}/bookings`),
    },
    pipeline: {
      get: () => request(PipelineDto, "/v1/pipeline"),
      put: (input: PipelineInput) => request(PipelineDto, "/v1/pipeline", { method: "PUT", body: input }),
      owners: () => request(LeadOwnerList, "/v1/pipeline/owners"),
      addOwner: (input: LeadOwnerInput) => request(LeadOwnerDto, "/v1/pipeline/owners", { method: "POST", body: input }),
      patchOwner: (id: string, patch: Partial<LeadOwnerInput>) => request(LeadOwnerDto, `/v1/pipeline/owners/${seg(id)}`, { method: "PATCH", body: patch }),
      scoring: () => request(LeadScoringDto, "/v1/pipeline/scoring"),
      patchScoring: (patch: LeadScoringPatch) => request(LeadScoringDto, "/v1/pipeline/scoring", { method: "PATCH", body: patch }),
      settings: () => request(LeadSettingsDto, "/v1/pipeline/settings"),
      patchSettings: (patch: LeadSettingsPatch) => request(LeadSettingsDto, "/v1/pipeline/settings", { method: "PATCH", body: patch }),
      templates: (query: { purpose?: string; channel?: string } = {}) => request(LeadTemplateList, "/v1/pipeline/templates", { query }),
      addTemplate: (input: LeadTemplateInput) => request(LeadTemplateDto, "/v1/pipeline/templates", { method: "POST", body: input }),
      patchTemplate: (id: string, patch: z.input<typeof LeadTemplatePatch>) => request(LeadTemplateDto, `/v1/pipeline/templates/${seg(id)}`, { method: "PATCH", body: patch }),
      sequences: () => request(LeadSequenceList, "/v1/pipeline/sequences"),
      addSequence: (input: z.input<typeof LeadSequenceInput>) => request(LeadSequenceDto, "/v1/pipeline/sequences", { method: "POST", body: input }),
      patchSequence: (id: string, patch: z.input<typeof LeadSequencePatch>) => request(LeadSequenceDto, `/v1/pipeline/sequences/${seg(id)}`, { method: "PATCH", body: patch }),
      calendar: () => request(LeadCalendarDto, "/v1/pipeline/calendar"),
      connectCalendar: () => request(CalendarConnectUrl, "/v1/pipeline/calendar/connect", { method: "POST", body: {} }),
      disconnectCalendar: () => request(LeadCalendarDto, "/v1/pipeline/calendar", { method: "DELETE" }),
      funnel: (query: LeadFunnelQuery) => request(LeadFunnelDto, "/v1/pipeline/funnel", { query }),
    },
    forms: {
      list: () => request(LeadFormList, "/v1/lead-forms"),
      get: (id: string) => request(LeadFormDto, `/v1/lead-forms/${seg(id)}`),
      add: (input: z.input<typeof LeadFormInput>) => request(LeadFormDto, "/v1/lead-forms", { method: "POST", body: input }),
      patch: (id: string, patch: z.input<typeof LeadFormPatch>) => request(LeadFormDto, `/v1/lead-forms/${seg(id)}`, { method: "PATCH", body: patch }),
    },
    landings: {
      list: () => request(LandingPageList, "/v1/lead-landings"),
      add: (input: LandingPageInput) => request(LandingPageDto, "/v1/lead-landings", { method: "POST", body: input }),
      patch: (id: string, patch: LandingPagePatch) => request(LandingPageDto, `/v1/lead-landings/${seg(id)}`, { method: "PATCH", body: patch }),
      status: (id: string, status: "published" | "draft" | "archived") => request(LandingPageDto, `/v1/lead-landings/${seg(id)}/status`, { method: "POST", body: { status } }),
    },
    consent: {
      people: (query: { q?: string; contactId?: string; cursor?: string; limit?: number }) => request(ConsentPeoplePage, "/v1/consents", { query }),
      suppressions: (query: { family?: string; reason?: string; q?: string; cursor?: string; limit?: number }) => request(SuppressionList, "/v1/messaging/suppressions", { query }),
      suppress: (input: SuppressionAddInput) => request(SuppressionAdded, "/v1/messaging/suppressions", { method: "POST", body: input }),
      lift: (id: string) => sendEmpty(`/v1/messaging/suppressions/${seg(id)}`, "DELETE"),
      requests: () => request(DsarRequestList, "/v1/dsar/requests"),
      exportPerson: (contactId: string) => request(DsarExportDto, "/v1/dsar/export", { method: "POST", body: { contactId } }),
      erasePerson: (contactId: string) => request(DsarRequestDto, "/v1/dsar/erase", { method: "POST", body: { contactId } }),
    },
    publish: {
      now: (postId: string) => request(Anything, `/v1/publish/posts/${seg(postId)}/publish`, { method: "POST" }),
      receipts: (query: { channelId?: string; limit?: number }) => request(ReceiptPage, "/v1/publish-receipts", { query }),
      manualCards: (state: "pending" | "published" | "all") => request(ManualCardPage, "/v1/manual-cards", { query: { state, limit: 100 } }),
      resolve: (postId: string, input: ResolveReviewInput) => request(Anything, `/v1/publish/posts/${seg(postId)}/resolve`, { method: "POST", body: input }),
      markManual: (postId: string, input: z.input<typeof MarkManualPublishedInput>) => request(ManualCardDto, `/v1/manual-cards/${seg(postId)}/mark-published`, { method: "POST", body: input }),
    },
    ads: {
      connections: () => request(AdConnectionPage, "/v1/ads/connections", { query: { limit: 100 } }),
      createConnection: (input: { platform: "meta" | "google"; name: string }) => request(AdConnectionDto, "/v1/ads/connections", { method: "POST", body: input }),
      oauthStart: (id: string) => request(AdOAuthStartDto, `/v1/ads/connections/${seg(id)}/oauth/start`, { method: "POST", body: {} }),
      accounts: (id: string) => request(AdAccountList, `/v1/ads/connections/${seg(id)}/accounts`),
      selectAccount: (id: string, externalId: string) => request(AdConnectionDto, `/v1/ads/connections/${seg(id)}/select-account`, { method: "POST", body: { externalId } }),
      systemUserToken: (id: string, input: { token: string; externalAccountId?: string }) => request(AdConnectionDto, `/v1/ads/connections/${seg(id)}/system-user-token`, { method: "POST", body: input }),
      testConnection: (id: string) => request(AdConnectionTestDto, `/v1/ads/connections/${seg(id)}/test`, { method: "POST" }),
      syncConnection: (id: string) => request(AdSyncReportDto, `/v1/ads/connections/${seg(id)}/sync`, { method: "POST" }),
      disconnect: (id: string) => request(AdConnectionDto, `/v1/ads/connections/${seg(id)}/disconnect`, { method: "POST" }),
      assets: (id: string) => request(AdAccountAssetsDto, `/v1/ads/connections/${seg(id)}/assets`),
      setAssets: (id: string, input: Record<string, string | null>) => request(AdAccountAssetsDto, `/v1/ads/connections/${seg(id)}/assets`, { method: "PUT", body: input }),
      pages: (id: string) => request(AdPageList, `/v1/ads/connections/${seg(id)}/pages`),
      limits: (id: string) => request(AdConnectionLimitsDto, `/v1/ads/connections/${seg(id)}/limits`),

      campaigns: () => request(AdCampaignPage, "/v1/ads/campaigns", { query: { limit: 100 } }),
      campaignTargets: (id: string, input: AdCampaignTargetsInput) => request(AdCampaignDto, `/v1/ads/campaigns/${seg(id)}/targets`, { method: "PUT", body: input }),
      adSets: (campaignId: string) => request(AdSetPage, "/v1/ads/ad-sets", { query: { campaignId, limit: 100 } }),
      ads: (adSetId: string) => request(AdPage, "/v1/ads/ads", { query: { adSetId, limit: 100 } }),
      keywords: (adSetId: string) => request(AdKeywordPage, "/v1/ads/keywords", { query: { adSetId, limit: 100 } }),
      metrics: (query: { level: "campaign" | "ad_set" | "ad"; from: string; to: string }) => request(AdMetricTotals, "/v1/ads/metrics", { query }),
      pause: (level: string, id: string, reason?: string) => request(AdActionDto, `/v1/ads/entities/${seg(level)}/${seg(id)}/pause`, { method: "POST", body: reason ? { reason } : {} }),
      start: (level: string, id: string, reason?: string) => request(AdActionDto, `/v1/ads/entities/${seg(level)}/${seg(id)}/start`, { method: "POST", body: reason ? { reason } : {} }),
      budget: (level: string, id: string, input: { amountMinor: number; currency: string; reason?: string }) => request(AdActionDto, `/v1/ads/entities/${seg(level)}/${seg(id)}/budget`, { method: "POST", body: input }),
      emergencyStop: (reason?: string) => request(EmergencyStopResultDto, "/v1/ads/emergency-stop", { method: "POST", body: reason ? { reason } : {} }),
      emergencyRelease: (reason: string) => request(AdActionDto, "/v1/ads/emergency-stop/release", { method: "POST", body: { reason } }),
      guards: () => request(AdGuardStatusDto, "/v1/ads/guards"),
      setGuards: (patch: { capCurrency?: string; dailyCapMinor?: number | null; monthlyCapMinor?: number | null; channelMonthlyCaps?: Partial<Record<AdPlatform, number>>; targetCostPerLeadMinor?: number | null; targetCostPerClientMinor?: number | null; maxBudgetGrowthPct?: number; autoPauseEnabled?: boolean }) => request(AdGuardSettingsDto, "/v1/ads/guards", { method: "PATCH", body: patch }),
      runGuards: () => request(AdGuardRunDto, "/v1/ads/guards/run", { method: "POST" }),
      actions: (query: { connectionId?: string; entityId?: string; status?: string; action?: string; limit?: number; cursor?: string }) => request(AdActionPage, "/v1/ads/actions", { query }),
      alerts: () => request(AdAlertPage, "/v1/ads/alerts", { query: { open: "true", limit: 50 } }),
      ackAlert: (id: string) => request(AdAlertDto, `/v1/ads/alerts/${seg(id)}/ack`, { method: "POST" }),

      cells: () => request(AdCellPage, "/v1/ads/cells", { query: { limit: 100 } }),
      cell: (id: string) => request(AdCellDto, `/v1/ads/cells/${seg(id)}`),
      createCell: (input: object) => request(AdCellDto, "/v1/ads/cells", { method: "POST", body: input }),
      patchCell: (id: string, patch: { abcMode?: "advise" | "stop_losers"; autoScale?: boolean; scaleCeilingMinor?: number }) => request(AdCellDto, `/v1/ads/cells/${seg(id)}`, { method: "PATCH", body: patch }),
      resumeCell: (id: string) => request(AdCellDto, `/v1/ads/cells/${seg(id)}/resume`, { method: "POST" }),
      startCell: (id: string) => request(AdStartResultDto, `/v1/ads/cells/${seg(id)}/start`, { method: "POST" }),
      stopCell: (id: string) => request(AdCellDto, `/v1/ads/cells/${seg(id)}/stop`, { method: "POST" }),
      evaluateCell: (id: string) => request(AdEvaluationDto, `/v1/ads/cells/${seg(id)}/evaluate`, { method: "POST" }),
      scaleCell: (id: string) => request(AdCellDecisionDto, `/v1/ads/cells/${seg(id)}/scale`, { method: "POST" }),
      decisions: (id: string) => request(AdCellDecisionPage, `/v1/ads/cells/${seg(id)}/decisions`, { query: { limit: 100 } }),
      scheduleSuggestion: () => request(AdScheduleSuggestionDto, "/v1/ads/schedule-suggestion"),
      holds: (query: { status?: AdHoldStatus; cursor?: string } = {}) => request(AdHoldPage, "/v1/ads/holds", { query: { limit: 50, ...query } }),
      startHold: (id: string) => request(AdStartResultDto, `/v1/ads/holds/${seg(id)}/start`, { method: "POST" }),
      dismissHold: (id: string) => request(AdHoldDto, `/v1/ads/holds/${seg(id)}/dismiss`, { method: "POST" }),

      wizards: () => request(AdWizardPage, "/v1/ads/wizards", { query: { limit: 100 } }),
      wizard: (id: string) => request(AdWizardDto, `/v1/ads/wizards/${seg(id)}`),
      createWizard: (input: object) => request(AdWizardDto, "/v1/ads/wizards", { method: "POST", body: input }),
      patchWizard: (id: string, patch: object) => request(AdWizardDto, `/v1/ads/wizards/${seg(id)}`, { method: "PATCH", body: patch }),
      suggestKeywords: (id: string) => request(AdWizardDto, `/v1/ads/wizards/${seg(id)}/suggest-keywords`, { method: "POST" }),
      suggestText: (id: string) => request(AdWizardDto, `/v1/ads/wizards/${seg(id)}/suggest-text`, { method: "POST" }),
      reviewWizard: (id: string) => request(AdWizardReviewDto, `/v1/ads/wizards/${seg(id)}/review`),
      launchWizard: (id: string) => request(AdWizardDto, `/v1/ads/wizards/${seg(id)}/launch`, { method: "POST" }),
      startWizard: (id: string) => request(AdStartResultDto, `/v1/ads/wizards/${seg(id)}/start`, { method: "POST" }),

      promotions: () => request(AdPromotionPage, "/v1/ads/promotions", { query: { limit: 100 } }),
      promotion: (id: string) => request(AdPromotionDto, `/v1/ads/promotions/${seg(id)}`),
      createPromotion: (input: object) => request(AdPromotionDto, "/v1/ads/promotions", { method: "POST", body: input }),
      patchPromotion: (id: string, patch: object) => request(AdPromotionDto, `/v1/ads/promotions/${seg(id)}`, { method: "PATCH", body: patch }),
      launchPromotion: (id: string) => request(AdPromotionDto, `/v1/ads/promotions/${seg(id)}/launch`, { method: "POST" }),
      startPromotion: (id: string) => request(AdStartResultDto, `/v1/ads/promotions/${seg(id)}/start`, { method: "POST" }),
      stopPromotion: (id: string) => request(AdPromotionDto, `/v1/ads/promotions/${seg(id)}/stop`, { method: "POST" }),

      costCap: (level: string, id: string, input: { amountMinor: number | null; currency: string; reason?: string }) => request(AdActionDto, `/v1/ads/entities/${seg(level)}/${seg(id)}/cost-cap`, { method: "POST", body: input }),
      role: (id: string, input: { role: "test" | "scale" | "retargeting" | null; reason?: string }) => request(AdActionDto, `/v1/ads/campaigns/${seg(id)}/role`, { method: "POST", body: input }),
      dailyMetrics: (query: { connectionId: string; level: string; externalId: string; from: string; to: string }) => request(AdDailyMetrics, "/v1/ads/metrics/daily", { query }),
      checkRsa: (input: object) => request(AdRsaCheckDto, "/v1/ads/rsa/check", { method: "POST", body: input }),
      createRsa: (input: object) => request(AdRsaDto, "/v1/ads/rsa", { method: "POST", body: input }),

      productAdOptions: (catalogId?: string) => request(AdProductOptionsDto, "/v1/ads/product-ads/options", { query: catalogId ? { catalogId } : {} }),
      productAdPreview: (input: object) => request(AdProductSetDto, "/v1/ads/product-ads/preview", { method: "POST", body: input }),
      productAds: () => request(AdProductAdPage, "/v1/ads/product-ads", { query: { limit: 100 } }),
      productAd: (id: string) => request(AdProductAdDto, `/v1/ads/product-ads/${seg(id)}`),
      createProductAd: (input: object) => request(AdProductAdDto, "/v1/ads/product-ads", { method: "POST", body: input }),
      patchProductAd: (id: string, patch: object) => request(AdProductAdDto, `/v1/ads/product-ads/${seg(id)}`, { method: "PATCH", body: patch }),
      launchProductAd: (id: string) => request(AdProductAdDto, `/v1/ads/product-ads/${seg(id)}/launch`, { method: "POST" }),
      startProductAd: (id: string) => request(AdStartResultDto, `/v1/ads/product-ads/${seg(id)}/start`, { method: "POST" }),
      stopProductAd: (id: string) => request(AdProductAdDto, `/v1/ads/product-ads/${seg(id)}/stop`, { method: "POST" }),

      formTemplates: (language: string) => request(AdFormTemplateList, "/v1/ads/form-templates", { query: { language } }),
      forms: () => request(AdFormPage, "/v1/ads/forms", { query: { limit: 100 } }),
      createForm: (input: object) => request(AdFormDto, "/v1/ads/forms", { method: "POST", body: input }),
      retryForm: (id: string) => request(AdFormDto, `/v1/ads/forms/${seg(id)}/retry`, { method: "POST" }),
      audiences: () => request(AdAudiencePage, "/v1/ads/audiences", { query: { limit: 100 } }),
      createAudience: (input: object) => request(AdAudienceDto, "/v1/ads/audiences", { method: "POST", body: input }),
      refreshAudience: (id: string) => request(AdAudienceRefreshDto, `/v1/ads/audiences/${seg(id)}/refresh`, { method: "POST" }),
      retryAudience: (id: string) => request(AdAudienceDto, `/v1/ads/audiences/${seg(id)}/retry`, { method: "POST" }),
      catalogs: () => request(AdCatalogPage, "/v1/ads/catalogs", { query: { limit: 100 } }),
      createCatalog: (input: { connectionId: string; name: string }) => request(AdCatalogDto, "/v1/ads/catalogs", { method: "POST", body: input }),
      syncCatalog: (id: string) => request(AdCatalogSyncDto, `/v1/ads/catalogs/${seg(id)}/sync`, { method: "POST" }),
    },
    tracking: {
      config: (tenantId: string) => request(TrackingConfigDto, `/v1/tenants/${seg(tenantId)}/tracking`),
      update: (tenantId: string, patch: object) => request(TrackingConfigDto, `/v1/tenants/${seg(tenantId)}/tracking`, { method: "PATCH", body: patch }),
      conversions: (tenantId: string, query: Record<string, string | number | undefined>) => request(ConversionPage, `/v1/tenants/${seg(tenantId)}/tracking/conversions`, { query }),
      retrySend: (tenantId: string, conversionId: string, destination: string) => request(ConversionDto, `/v1/tenants/${seg(tenantId)}/tracking/conversions/${seg(conversionId)}/sends/${seg(destination)}/retry`, { method: "POST" }),
      summary: (tenantId: string, query: { from?: string; to?: string }) => request(ConversionSummary, `/v1/tenants/${seg(tenantId)}/tracking/summary`, { query }),
      funnel: (tenantId: string, query: { from?: string; to?: string }) => request(TrackingFunnel, `/v1/tenants/${seg(tenantId)}/tracking/funnel`, { query }),
      consent: (tenantId: string, query: { from?: string; to?: string }) => request(ConsentSummary, `/v1/tenants/${seg(tenantId)}/tracking/consent`, { query }),
      testEvent: (tenantId: string) => request(TestEventResult, `/v1/tenants/${seg(tenantId)}/tracking/test-event`, { method: "POST" }),
    },
    billing: {
      status: () => request(BillingStatusDto, "/v1/billing/status"),
      get: () => request(BillingDto, "/v1/billing"),
      /** The first payment: `url` is the Stripe page; `joined` means the workspace went onto the company's live subscription without one. */
      checkout: (body: CheckoutRequest) => request(CheckoutResult, "/v1/billing/checkout", { method: "POST", body }),
      /** A higher plan applies now, prorated; a lower one comes back as `pendingPlan` from the end of the paid period. */
      changePlan: (body: PlanChangeRequest) => request(PlanChangeResult, "/v1/billing/plan", { method: "POST", body }),
      cancel: () => request(CancelResult, "/v1/billing/cancel", { method: "POST", body: {} }),
      resume: () => request(CancelResult, "/v1/billing/resume", { method: "POST", body: {} }),
      /** The company's portal: its card and invoices only. */
      portal: () => request(RedirectDto, "/v1/billing/portal", { method: "POST", body: {} }),
      /** „Repornește reclamele”: the ads the trial's stop paused, started again through the guards once paid. 402 while stopped. */
      restartAds: () => request(RestartAdsResult, "/v1/billing/restart-ads", { method: "POST", body: {} }),
      /** To another company of the same account: `joined` when that company pays already, else its Checkout `url`. No screen yet. */
      move: (body: MoveWorkspaceRequest) => request(CheckoutResult, "/v1/billing/move", { method: "POST", body }),
    },
    /** The account's companies that pay: reachable from a workspace session too. */
    companies: {
      list: () => request(CompanyList, "/v1/account/companies"),
      lookup: (query: CompanyLookupQuery) => request(CompanyLookupDto, "/v1/account/companies/lookup", { query }),
      create: (body: NewCompanyRequest) => request(CompanyDto, "/v1/account/companies", { method: "POST", body }),
      patch: (id: string, body: CompanyPatch) => request(CompanyDto, `/v1/account/companies/${seg(id)}`, { method: "PATCH", body }),
      check: (id: string) => request(CompanyDto, `/v1/account/companies/${seg(id)}/check`, { method: "POST", body: {} }),
      portal: (id: string) => request(RedirectDto, `/v1/account/companies/${seg(id)}/portal`, { method: "POST", body: {} }),
    },
    aiKeys: {
      status: () => request(AiKeysStatusDto, "/v1/ai-keys"),
      save: (body: AiKeySaveRequest) => request(AiKeyDto, "/v1/ai-keys", { method: "POST", body }),
      test: (provider: AiKeyProvider) => request(AiKeyDto, `/v1/ai-keys/${seg(provider)}/test`, { method: "POST", body: {} }),
      remove: (provider: AiKeyProvider) => sendEmpty(`/v1/ai-keys/${seg(provider)}`, "DELETE"),
      choose: (choice: AiKeyChoice) => request(AiKeysStatusDto, "/v1/ai-keys/choice", { method: "PUT", body: { choice } }),
      // s10-keys-b: the guided start's key step was shown.
      stepShown: () => request(AiKeyStepDto, "/v1/ai-keys/step/shown", { method: "POST", body: {} }),
    },
    // s10-keys-b
    smsProvider: {
      status: () => request(SmsStatusDto, "/v1/sms-provider"),
      save: (body: SmsProviderSaveRequest) => request(SmsStatusDto, "/v1/sms-provider", { method: "PUT", body }),
      test: () => request(SmsStatusDto, "/v1/sms-provider/test", { method: "POST", body: {} }),
      remove: () => sendEmpty("/v1/sms-provider", "DELETE"),
    },
    aiUsage: {
      days: (query: { month?: string } = {}) => request(AiUsageDaysResponse, "/v1/ai-usage/days", { query }),
      calls: (query: { cursor?: string; limit?: number; day?: string; purpose?: string; model?: string } = {}) => request(AiUsageCallPage, "/v1/ai-usage/calls", { query }),
    },
    agencies: {
      get: (id: string) => request(AgencyDto, `/v1/agencies/${seg(id)}`),
      brand: (id: string) => request(AgencyBrandResponse, `/v1/agencies/${seg(id)}/brand`),
      putBrand: (id: string, body: BrandInput) => request(AgencyBrandResponse, `/v1/agencies/${seg(id)}/brand`, { method: "PUT", body }),
      clients: (id: string, cursor?: string) => request(AgencyTenantPage, `/v1/agencies/${seg(id)}/tenants`, { query: { cursor } }),
      members: (id: string, cursor?: string) => request(AgencyMemberPage, `/v1/agencies/${seg(id)}/members`, { query: { cursor } }),
      changeRole: (id: string, memberId: string, body: AgencyMemberPatch) => request(AgencyMemberDto, `/v1/agencies/${seg(id)}/members/${seg(memberId)}`, { method: "PATCH", body }),
      remove: (id: string, memberId: string) => sendEmpty(`/v1/agencies/${seg(id)}/members/${seg(memberId)}`, "DELETE"),
      invitations: (id: string, cursor?: string) => request(InvitationPage, `/v1/agencies/${seg(id)}/invitations`, { query: { cursor } }),
      invite: (id: string, body: InvitationRequest) => request(InvitationDto, `/v1/agencies/${seg(id)}/invitations`, { method: "POST", body }),
      resend: (id: string, invitationId: string) => request(InvitationDto, `/v1/agencies/${seg(id)}/invitations/${seg(invitationId)}/resend`, { method: "POST", body: {} }),
      revoke: (id: string, invitationId: string) => sendEmpty(`/v1/agencies/${seg(id)}/invitations/${seg(invitationId)}`, "DELETE"),
      audit: (id: string, query: Partial<AuditQuery> = {}) => request(AuditList, `/v1/agencies/${seg(id)}/audit`, { query }),
    },
    team: {
      members: (cursor?: string) => request(TeamMemberPage, "/v1/tenant/members", { query: { cursor } }),
      changeRole: (id: string, body: TeamMemberPatch) => request(TeamMemberDto, `/v1/tenant/members/${seg(id)}`, { method: "PATCH", body }),
      remove: (id: string) => sendEmpty(`/v1/tenant/members/${seg(id)}`, "DELETE"),
      invitations: (cursor?: string) => request(InvitationPage, "/v1/tenant/invitations", { query: { cursor } }),
      invite: (body: InvitationRequest) => request(InvitationDto, "/v1/tenant/invitations", { method: "POST", body }),
      resend: (id: string) => request(InvitationDto, `/v1/tenant/invitations/${seg(id)}/resend`, { method: "POST", body: {} }),
      revoke: (id: string) => sendEmpty(`/v1/tenant/invitations/${seg(id)}`, "DELETE"),
      approvers: () => request(ApproversDto, "/v1/tenant/approvers"),
      setApprovers: (body: SetApproversRequest) => request(ApproversDto, "/v1/tenant/approvers", { method: "PUT", body }),
    },
    notifications: {
      get: () => request(NotifyPreferencesDto, "/v1/me/notifications"),
      setCell: (body: NotifyCellPatch) => request(NotifyPreferencesDto, "/v1/me/notifications/events", { method: "PATCH", body }),
      setQuietHours: (body: QuietHoursRequest) => request(NotifyPreferencesDto, "/v1/me/notifications/quiet-hours", { method: "PUT", body }),
      setPhone: (body: NotifyPhoneRequest) => request(NotifyPreferencesDto, "/v1/me/notifications/phone", { method: "PUT", body }),
      linkTelegram: () => request(TelegramLinkDto, "/v1/me/notifications/telegram", { method: "POST", body: {} }),
      unlinkTelegram: () => request(NotifyPreferencesDto, "/v1/me/notifications/telegram", { method: "DELETE" }),
      subscribePush: (body: PushSubscribeRequest) => request(PushSubscribedDto, "/v1/me/notifications/push", { method: "POST", body }),
      removePush: (id: string) => request(NotifyPreferencesDto, `/v1/me/notifications/push/${seg(id)}`, { method: "DELETE" }),
      handoff: () => request(PushHandoffDto, "/v1/me/notifications/push/handoff", { method: "POST", body: {} }),
    },
    account: {
      brand: (domain?: string) => request(PublicBrandDto, "/v1/public/brand", { anonymous: true, query: { domain } }),
      /** Asks for the letter with the code and the link. The same answer for any address; only this browser may type the code. */
      requestCode: (body: SignInCodeRequest) => request(SignInCodeAccepted, "/v1/public/account/code", { method: "POST", body, anonymous: true }),
      verifyCode: (body: SignInCodeVerify) => request(AccountSignedIn, "/v1/public/account/code/verify", { method: "POST", body, anonymous: true, credentials: true }),
      /** The link from the same letter, on any device. */
      verify: (token: string) => request(AccountSignedIn, "/v1/public/account/verify", { method: "POST", body: { token }, anonymous: true, credentials: true }),
      /** The cookie's turn: a new access token for a device that stays signed in. */
      refresh: (body: AccountRefreshRequest) => request(AccountSignedIn, "/v1/public/account/refresh", { method: "POST", body, anonymous: true, credentials: true }),
      createWorkspace: (body: WorkspaceRequest) => request(DirectSession, "/v1/account/workspaces", { method: "POST", body }),
      devices: () => request(AccountDeviceList, "/v1/account/devices"),
      signOutDevice: (id: string) => request(AccountSignedOut, `/v1/account/devices/${seg(id)}/sign-out`, { method: "POST", body: {}, credentials: true }),
      signOutEverywhere: () => request(AccountSignedOut, "/v1/account/devices/sign-out-all", { method: "POST", body: {}, credentials: true }),
      signOut: () => request(AccountSignedOut, "/v1/account/sign-out", { method: "POST", body: {}, credentials: true }),
      previewInvitation: (token: string) => request(InvitationPreview, "/v1/public/account/invitations/preview", { method: "POST", body: { token }, anonymous: true }),
      acceptInvitation: (body: InvitationAcceptRequest) => request(DirectSession, "/v1/public/account/invitations/accept", { method: "POST", body, anonymous: true }),
      /** A code made in another tab: brings today's session here, once. */
      handoff: (code: string) => request(DirectSession, "/v1/public/account/handoff", { method: "POST", body: { code }, anonymous: true }),
      /** A tapped notice, in a browser with no session of its own. */
      deviceSession: (deviceToken: string) => request(DirectSession, "/v1/public/account/device-session", { method: "POST", body: { deviceToken }, anonymous: true }),
      /** Reads the session a saved token belongs to, so a reload keeps the person signed in. */
      restore: (token: string) => request(AccountSignedIn, "/v1/account/session", { anonymous: true, bearer: token }),
      tenants: () => request(AccountTenantList, "/v1/account/tenants"),
      addTenant: (body: AgencyTenantRequest) => request(DirectSession, "/v1/account/tenants", { method: "POST", body }),
      switchTenant: (id: string) => request(DirectSession, `/v1/account/tenants/${seg(id)}/session`, { method: "POST", body: {} }),
      /** The same move while the store is still taking the renewed session on: nothing holds this token yet. */
      switchTenantWith: (bearer: string, id: string) => request(DirectSession, `/v1/account/tenants/${seg(id)}/session`, { method: "POST", body: {}, bearer }),
      // s10-login-b: Google, Apple and Facebook, and „Contul tău → Metode de autentificare”.
      /** A trip to the provider to sign in: the tab keeps `signInId` and goes to `authorizeUrl`. */
      oauthStart: (provider: LoginProvider, body: OAuthSignInStartRequest) => request(OAuthStarted, `/v1/public/account/oauth/${seg(provider)}/start`, { method: "POST", body, anonymous: true }),
      /** The way back, with the cookie of a device that stays signed in. */
      oauthComplete: (body: OAuthCompleteRequest) => request(OAuthCompleted, "/v1/public/account/oauth/complete", { method: "POST", body, anonymous: true, credentials: true }),
      /** The address a Facebook sign-in without one goes on with; a code goes to it. */
      oauthEmail: (body: OAuthEmailRequest) => request(SignInCodeAccepted, "/v1/public/account/oauth/email", { method: "POST", body, anonymous: true }),
      deletionStatus: (code: string) => request(AccountDeletionStatus, `/v1/public/account/deletion/${seg(code)}`, { anonymous: true }),
      methods: () => request(AccountMethodList, "/v1/account/methods"),
      /** A trip to the provider that adds it to the account signed in. 403 `reauth_required` when the session is not fresh. */
      startMethod: (provider: LoginProvider, body: OAuthStartRequest) => request(OAuthStarted, `/v1/account/methods/${seg(provider)}/start`, { method: "POST", body }),
      removeMethod: (id: string) => request(AccountMethodList, `/v1/account/methods/${seg(id)}`, { method: "DELETE" }),
      /** A code to the account's own address, which makes the session fresh again. */
      reauthCode: (body: AccountReauthRequest) => request(SignInCodeAccepted, "/v1/account/reauth/code", { method: "POST", body }),
      reauthVerify: (body: AccountReauthVerify) => request(AccountSignedIn, "/v1/account/reauth/verify", { method: "POST", body, credentials: true }),
      // s10-mcp-a: an assistant asking to connect, read and decided on /connect with the person's account token.
      // The id goes in the query and the body, never the path: the request log writes paths.
      connectRequest: (id: string) => request(ConnectRequestDto, "/v1/account/oauth/requests", { query: { id } }),
      decideConnect: (id: string, body: ConnectDecisionRequest) => request(ConnectDecisionDto, "/v1/account/oauth/requests/decision", { method: "POST", body: { id, ...body } }),
      // s10-mcp-b: „Asistenți conectați”, the person's own connections in every workspace.
      assistants: () => request(McpConnectionList, "/v1/account/oauth/connections"),
      disconnectAssistant: (id: string) => request(McpConnectionDto, `/v1/account/oauth/connections/${seg(id)}/revoke`, { method: "POST", body: {} }),
    },
    platform: {
      login: (body: LoginLinkRequest) => request(AccountAccepted, "/v1/public/platform/login", { method: "POST", body, anonymous: true }),
      /** Only the first of two steps: what comes back opens nothing until a code goes with it. */
      verify: (token: string) => request(PlatformLinkResult, "/v1/public/platform/verify", { method: "POST", body: { token }, anonymous: true }),
      enroll: (body: SecondFactorRequest) => request(PlatformEnrolled, "/v1/public/platform/second-factor/enroll", { method: "POST", body, anonymous: true }),
      secondFactor: (body: SecondFactorRequest) => request(PlatformSession, "/v1/public/platform/second-factor/verify", { method: "POST", body, anonymous: true }),
      audit: (query: { cursor?: string; limit?: number } = {}) => request(PlatformAuditPage, "/v1/admin/audit", { query }),
      agencies: (query: { hostId?: string; q?: string; cursor?: string; limit?: number } = {}) => request(AdminAgencyPage, "/v1/admin/agencies", { query }),
      setTenantAgency: (id: string, body: AdminTenantAgencyPut) => request(AdminTenantDto, `/v1/admin/tenants/${seg(id)}/agency`, { method: "PUT", body }),
      me: () => request(PlatformOperatorDto, "/v1/admin/me"),
      hosts: () => request(AdminHostList, "/v1/admin/hosts"),
      patchHost: (id: string, body: AdminHostPatch) => request(AdminHostDto, `/v1/admin/hosts/${seg(id)}`, { method: "PATCH", body }),
      hostLive: (id: string) => request(AdminLiveResponse, `/v1/admin/hosts/${seg(id)}/live`),
      setHostLive: (id: string, body: AdminLiveRequest) => request(AdminLiveResponse, `/v1/admin/hosts/${seg(id)}/live`, { method: "PUT", body }),
      reconciliation: (id: string, month?: string) => request(ReconciliationDto, `/v1/admin/hosts/${seg(id)}/reconciliation`, { query: { month } }),
      /** The same reconciliation as a file. */
      async reconciliationCsv(id: string, month?: string): Promise<string> {
        const res = await send(`/v1/admin/hosts/${seg(id)}/reconciliation/csv`, { query: { month } });
        if (!res.ok) throw await readError(res);
        return res.text();
      },
      liftEmailStop: (id: string, body: LiftEmailStopRequest) => request(AdminTenantDto, `/v1/admin/tenants/${seg(id)}/email-stop/lift`, { method: "POST", body }),
      tenants: (query: { hostId?: string; q?: string; billingMode?: BillingMode; emailStopped?: "true"; cursor?: string }) => request(AdminTenantPage, "/v1/admin/tenants", { query }),
      aiGrant: (id: string) => request(AdminAiGrantDto, `/v1/admin/tenants/${seg(id)}/ai-grant`),
      setAiGrant: (id: string, body: AdminAiGrantPut) => request(AdminAiGrantDto, `/v1/admin/tenants/${seg(id)}/ai-grant`, { method: "PUT", body }),
      setBillingMode: (id: string, body: SetBillingModeRequest) => request(AdminTenantDto, `/v1/admin/tenants/${seg(id)}/billing-mode`, { method: "PUT", body }),
      setPlan: (id: string, body: SetPlanRequest) => request(AdminTenantDto, `/v1/admin/tenants/${seg(id)}/plan`, { method: "PUT", body }),
      setStatus: (id: string, body: SetTenantStatusRequest) => request(AdminTenantDto, `/v1/admin/tenants/${seg(id)}/status`, { method: "PATCH", body }),
      tenantLive: (id: string) => request(AdminLiveResponse, `/v1/admin/tenants/${seg(id)}/live`),
      setTenantLive: (id: string, body: AdminLiveRequest) => request(AdminLiveResponse, `/v1/admin/tenants/${seg(id)}/live`, { method: "PUT", body }),
      usage: (month?: string) => request(AdminUsageDto, "/v1/admin/usage", { query: { month } }),
      readiness: () => request(ReadinessReportDto, "/v1/admin/readiness"),
      // s10-login-b: the host's own login methods (decision 31). Secrets go in and never come back.
      hostLogin: (id: string) => request(HostLoginSettingsDto, `/v1/admin/hosts/${seg(id)}/login`),
      putHostLogin: (id: string, body: HostLoginSettingsPut) => request(HostLoginSettingsDto, `/v1/admin/hosts/${seg(id)}/login`, { method: "PUT", body }),
    },
    customers: {
      list: (query: { search?: string; lifecycle?: string; channel?: string; cursor?: string; limit?: number }) => request(CustomerPage, "/v1/customers", { query }),
      get: (id: string) => request(CustomerCardDto, `/v1/customers/${seg(id)}`),
      create: (input: z.input<typeof CustomerPersonCreate>) => request(CustomerSaveResult, "/v1/customers", { method: "POST", body: input }),
      update: (id: string, input: z.input<typeof CustomerPersonPatch>) => request(CustomerSaveResult, `/v1/customers/${seg(id)}`, { method: "PATCH", body: input }),
    },
    competitors: {
      list: () => request(CompetitorList, "/v1/competitors"),
      add: (input: CompetitorInput) => request(CompetitorDto, "/v1/competitors", { method: "POST", body: input }),
      update: (id: string, input: CompetitorPatch) => request(CompetitorDto, `/v1/competitors/${seg(id)}`, { method: "PATCH", body: input }),
      remove: (id: string) => sendEmpty(`/v1/competitors/${seg(id)}`, "DELETE"),
      ads: (query: Partial<CompetitorAdsQuery>) => request(CompetitorAdPage, "/v1/competitors/ads", { query }),
      useAd: (id: string, input: { title?: string; note?: string }) => request(TopicDto, `/v1/competitors/ads/${seg(id)}/use`, { method: "POST", body: input }),
      sync: (id?: string) => request(CompetitorSyncAnswer, id ? `/v1/competitors/${seg(id)}/sync` : "/v1/competitors/sync", { method: "POST", body: {} }),
    },
    news: {
      list: (query: Partial<ProductNewsListQuery>) => request(ProductNewsPage, "/v1/news", { query }),
      add: (input: ProductNewsInput) => request(ProductNewsDto, "/v1/news", { method: "POST", body: input }),
      update: (id: string, input: ProductNewsPatch) => request(ProductNewsDto, `/v1/news/${seg(id)}`, { method: "PATCH", body: input }),
      toTopic: (id: string) => request(NewsToTopicResponse, `/v1/news/${seg(id)}/topic`, { method: "POST", body: {} }),
    },
    segments: {
      list: (query: { cursor?: string; limit?: number } = {}) => request(SegmentPage, "/v1/segments", { query }),
      get: (id: string) => request(SegmentDto, `/v1/segments/${seg(id)}`),
      count: (id: string) => request(SegmentCountDto, `/v1/segments/${seg(id)}/count`),
      presets: () => request(SegmentPresetList, "/v1/segments/presets"),
      options: () => request(SegmentOptionsDto, "/v1/segments/options"),
      preview: (rules: z.input<typeof SegmentRules>) => request(SegmentCountDto, "/v1/segments/preview", { method: "POST", body: rules }),
      create: (input: z.input<typeof SegmentCreate>) => request(SegmentDto, "/v1/segments", { method: "POST", body: input }),
      update: (id: string, input: z.input<typeof SegmentPatch>) => request(SegmentDto, `/v1/segments/${seg(id)}`, { method: "PATCH", body: input }),
      remove: (id: string) => sendEmpty(`/v1/segments/${seg(id)}`, "DELETE"),
    },
    proposals: {
      list: (query: { status?: string; cursor?: string; limit?: number } = {}) => request(ProposalPage, "/v1/proposals", { query }),
      accept: (id: string, input: z.input<typeof ProposalAccept>) => request(ProposalDto, `/v1/proposals/${seg(id)}/accept`, { method: "POST", body: input }),
      dismiss: (id: string) => request(ProposalDto, `/v1/proposals/${seg(id)}/dismiss`, { method: "POST", body: {} }),
    },
    dataSources: {
      get: () => request(DataSourcesDto, "/v1/data-sources"),
    },
    imports: {
      list: (query: { cursor?: string; limit?: number } = {}) => request(ImportPage, "/v1/imports", { query }),
      get: (id: string) => request(ImportDto, `/v1/imports/${seg(id)}`),
      upload: async (kind: string, file: File) => {
        const res = await sendRaw(`/v1/imports?kind=${encodeURIComponent(kind)}&fileName=${encodeURIComponent(file.name)}`, { method: "POST", file });
        const parsed = ImportDto.safeParse(await res.json().catch(() => undefined));
        if (!parsed.success) throw new ApiError("contract_mismatch", res.status);
        return parsed.data;
      },
      map: (id: string, input: z.input<typeof ImportMapping>) => request(ImportDto, `/v1/imports/${seg(id)}/mapping`, { method: "PUT", body: input }),
      preview: (id: string) => request(ImportPreviewDto, `/v1/imports/${seg(id)}/preview`, { method: "POST", body: {} }),
      apply: (id: string) => request(ImportDto, `/v1/imports/${seg(id)}/apply`, { method: "POST", body: {} }),
    },
    /** Post templates: first lines, buttons, signatures, hashtags, cards. `templates` above and below is messages. */
    contentTemplates: {
      list: (filter: { kind?: string; language?: string; includeArchived?: boolean } = {}) => {
        // Any value at all counts as yes on the API, so "no" is not asking.
        const { includeArchived, ...query } = filter;
        return request(TemplatePage, "/v1/templates", { query: includeArchived ? { ...query, includeArchived: "true" } : query });
      },
      shelf: () => request(TemplateShelfDto, "/v1/templates/shelf"),
      check: (input: TemplateCheckInput) => request(TemplateCheckDto, "/v1/templates/check", { method: "POST", body: input }),
      create: (input: TemplateInput) => request(TemplateDto, "/v1/templates", { method: "POST", body: input }),
      update: (id: string, patch: TemplatePatch) => request(TemplateDto, `/v1/templates/${seg(id)}`, { method: "PATCH", body: patch }),
      remove: (id: string) => sendEmpty(`/v1/templates/${seg(id)}`, "DELETE"),
      copy: (id: string) => request(TemplateDto, `/v1/templates/${seg(id)}/copy`, { method: "POST", body: {} }),
      preview: (id: string, values: Record<string, string> = {}) => request(TemplatePreviewDto, `/v1/templates/${seg(id)}/preview`, { method: "POST", body: { values } }),
    },
    profiles: {
      list: () => request(ProfilePage, "/v1/profiles", { query: { limit: 100 } }),
      create: (input: z.input<typeof ProfileInput>) => request(ProfileDto, "/v1/profiles", { method: "POST", body: input }),
      update: (id: string, patch: z.input<typeof ProfilePatch>) => request(ProfileDto, `/v1/profiles/${seg(id)}`, { method: "PATCH", body: patch }),
      remove: (id: string) => sendEmpty(`/v1/profiles/${seg(id)}`, "DELETE"),
    },
    angles: {
      list: (query: { status?: string } = {}) => request(AnglePage, "/v1/angles", { query: { limit: 100, ...query } }),
      create: (input: z.input<typeof AngleInput>) => request(AngleDto, "/v1/angles", { method: "POST", body: input }),
      update: (id: string, patch: z.input<typeof AnglePatch>) => request(AngleDto, `/v1/angles/${seg(id)}`, { method: "PATCH", body: patch }),
      remove: (id: string) => sendEmpty(`/v1/angles/${seg(id)}`, "DELETE"),
    },
    templates: {
      list: (query: { channel?: string; status?: string; language?: string } = {}) => request(MessageTemplateList, "/v1/message-templates", { query }),
      get: (id: string) => request(MessageTemplateDto, `/v1/message-templates/${seg(id)}`),
      create: (input: z.input<typeof TemplateCreateInput>) => request(MessageTemplateDto, "/v1/message-templates", { method: "POST", body: input }),
      update: (id: string, input: z.input<typeof TemplateUpdateInput>) => request(MessageTemplateDto, `/v1/message-templates/${seg(id)}`, { method: "PATCH", body: input }),
      archive: (id: string) => sendEmpty(`/v1/message-templates/${seg(id)}`, "DELETE"),
      generate: (input: z.input<typeof TemplateGenerateInput>) => request(MessageTemplateDto, "/v1/message-templates/generate", { method: "POST", body: input }),
      preview: (id: string, input: z.input<typeof TemplatePreviewInput>) => request(MessageTemplatePreviewDto, `/v1/message-templates/${seg(id)}/preview`, { method: "POST", body: input }),
      testSend: (id: string, input: z.input<typeof TemplateTestSendInput>) => request(TemplateTestSendResult, `/v1/message-templates/${seg(id)}/test-send`, { method: "POST", body: input }),
    },
    campaigns: {
      list: (query: { status?: string; channel?: string } = {}) => request(CampaignList, "/v1/campaigns", { query }),
      get: (id: string) => request(CampaignDto, `/v1/campaigns/${seg(id)}`),
      create: (input: z.input<typeof CampaignCreateInput>) => request(CampaignDto, "/v1/campaigns", { method: "POST", body: input }),
      update: (id: string, input: z.input<typeof CampaignUpdateInput>) => request(CampaignDto, `/v1/campaigns/${seg(id)}`, { method: "PATCH", body: input }),
      report: (id: string) => request(CampaignReportDto, `/v1/campaigns/${seg(id)}/report`),
      stats: (id: string) => request(CampaignStatsDto, `/v1/campaigns/${seg(id)}/stats`),
      schedule: (id: string, input: z.input<typeof CampaignScheduleInput>) => request(CampaignDto, `/v1/campaigns/${seg(id)}/schedule`, { method: "POST", body: input }),
      pause: (id: string) => request(CampaignDto, `/v1/campaigns/${seg(id)}/pause`, { method: "POST", body: {} }),
      resume: (id: string) => request(CampaignDto, `/v1/campaigns/${seg(id)}/resume`, { method: "POST", body: {} }),
      cancel: (id: string) => request(CampaignDto, `/v1/campaigns/${seg(id)}/cancel`, { method: "POST", body: {} }),
    },
    journeys: {
      list: () => request(JourneyList, "/v1/journeys"),
      get: (id: string) => request(JourneyDto, `/v1/journeys/${seg(id)}`),
      create: (input: z.input<typeof JourneyCreateInput>) => request(JourneyDto, "/v1/journeys", { method: "POST", body: input }),
      update: (id: string, input: z.input<typeof JourneyUpdateInput>) => request(JourneyDto, `/v1/journeys/${seg(id)}`, { method: "PATCH", body: input }),
      defaults: () => request(JourneyDefaultsResult, "/v1/journeys/defaults", { method: "POST", body: {} }),
      setStatus: (id: string, status: string) => request(JourneyDto, `/v1/journeys/${seg(id)}/status`, { method: "POST", body: { status } }),
      stats: (id: string) => request(JourneyStatsDto, `/v1/journeys/${seg(id)}/stats`),
      enrollments: (id: string, query: { status?: string; contactId?: string } = {}) => request(JourneyEnrollmentList, `/v1/journeys/${seg(id)}/enrollments`, { query }),
      enroll: (id: string, input: z.input<typeof JourneyEnrollInput>) => request(JourneyEnrollResult, `/v1/journeys/${seg(id)}/enrollments`, { method: "POST", body: input }),
      checkPeople: (id: string, contactIds: readonly string[]) => request(JourneyManualCheckDto, `/v1/journeys/${seg(id)}/enrollments/check`, { method: "POST", body: { contactIds } }),
      addPeople: (id: string, contactIds: readonly string[]) => request(JourneyManualEnrollDto, `/v1/journeys/${seg(id)}/enrollments/batch`, { method: "POST", body: { contactIds } }),
      steps: (id: string, enrollmentId: string) => request(JourneyStepRunList, `/v1/journeys/${seg(id)}/enrollments/${seg(enrollmentId)}/steps`),
      exit: (id: string, enrollmentId: string) => sendEmpty(`/v1/journeys/${seg(id)}/enrollments/${seg(enrollmentId)}`, "DELETE"),
    },
    referral: {
      program: () => request(ReferralProgramAnswer, "/v1/referral/program"),
      saveProgram: (input: z.input<typeof ReferralProgramInput>) => request(ReferralProgramDto, "/v1/referral/program", { method: "PUT", body: input }),
      stats: () => request(ReferralStatsDto, "/v1/referral/stats"),
      createCode: (contactId: string) => request(ReferralCodeDto, "/v1/referral/codes", { method: "POST", body: { contactId } }),
    },
    messaging: {
      overview: () => request(MessagingOverviewDto, "/v1/messaging/settings"),
      emailStanding: () => request(EmailStandingDto, "/v1/messaging/email-standing"),
      country: (country: string) => request(CountrySettingsDto, `/v1/messaging/settings/${seg(country)}`),
      saveCountry: (country: string, input: z.input<typeof ChannelSettingsInputDto>) => request(CountrySettingsDto, `/v1/messaging/settings/${seg(country)}`, { method: "PUT", body: input }),
      dryRuns: (query: { cursor?: string; limit?: number; sourceKind?: string; sourceId?: string } = {}) => request(DryRunMessageList, "/v1/messaging/dry-runs", { query }),
      domains: () => request(SendingDomainList, "/v1/messaging/domains"),
      addDomain: (domain: string) => request(SendingDomainDto, "/v1/messaging/domains", { method: "POST", body: { domain } }),
      checkDomain: (id: string) => request(SendingDomainDto, `/v1/messaging/domains/${seg(id)}/check`, { method: "POST", body: {} }),
      removeDomain: (id: string) => sendEmpty(`/v1/messaging/domains/${seg(id)}`, "DELETE"),
    },
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;

const MAX_RETRIES = 2;

/** A refused request will be refused again. Only a dropped connection or a server failure is worth another try. */
export function shouldRetry(failures: number, error: unknown): boolean {
  if (failures >= MAX_RETRIES) return false;
  return !(error instanceof ApiError) || error.status === 0 || error.status >= 500;
}
